import { FC, useContext, MouseEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { NextRouter, useRouter } from 'next/router';
import triggerTracking from '@blueprint/tracking';

import { SelectPropertyType } from './SelectPropertyType';
import { SelectBedsPriceForSale } from './SelectBedsPriceForSale';
import { SelectPriceToRent } from './SelectPriceToRent';
import { SelectBedsToRent } from './SelectBedsToRent';
import { SelectMaxTravelTime } from './SelectMaxTravelTime';
import { SelectTransportMode } from './SelectTransportMode';
import { SearchContext, SearchSource } from '@zoopla/listing-search-context';
import { SearchPageType } from '@zoopla/listing-search-context';
import { LocationSearchInput } from '../LocationSearchInput/LocationSearchInput';
import {
    filtersValuesSelector,
    isTravelTimeSearchSelector,
    locationSelector,
    identifierSelector,
    locationTextSelector
} from '@zoopla/redux-reducer-filters';
import { selectCurrentSortOrder } from '@zoopla/redux-reducer-sort-order';
import {
    UpdateButtonWithIcon,
    LocationFilterWrapper,
    MobileSectionHeadline
} from './SearchInitiationMobile.styles';
import { BuyRentTabs } from '../../../homepage/components';
import { Tab } from '../../../homepage/components/HeroContainer/HeroContainer.types';

import { SelectRadius } from './SelectRadius';
import {
    SearchInitationWrapper,
    StyledButtonMobile,
    StyledButtonWrapperMobile,
    HousePricesLocationWrapper
} from '../SearchInitiation/SearchInitiation.styles';
import { searchHousePrices, searchProperties } from 'search/helpers';
import { SearchSmall } from '@blueprint/icons';

const SEARCH_BUTTON_TEXT = 'Search';
const PRICE_RANGE = 'Price range';
const BEDROOMS = 'Bedrooms';
const EVENT = 'GAEvent';
const CATEGORY = 'search_filters';

type Props = {
    showRadius?: boolean;
    updateResultsText: string;
    searchType: Tab;
    eventCategory: 'homepage' | 'search results' | 'new_homes';
    searchSource: SearchSource;
    excludePageSize?: boolean;
    onSearchClick?: (args: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        filters: any;
        router: NextRouter;
    }) => void;
    hideTabs?: boolean;
    hidePropertyType?: boolean;
};

export const SearchInitiationMobile: FC<Props> = (props) => {
    const {
        showRadius = true,
        updateResultsText,
        searchType,
        eventCategory,
        searchSource,
        excludePageSize = false,
        onSearchClick,
        hideTabs = false,
        hidePropertyType = false
    } = props;
    const router = useRouter();
    const { state, dispatch } = useContext(SearchContext);
    const { userAlertId } = state;

    const { selectedValue: locationValue } = useSelector(locationSelector);
    const { selectedValue: identifier } = useSelector(identifierSelector);
    const { selectedValue: locationTextValue } =
        useSelector(locationTextSelector);
    const [hasSubmittedLocationSearch, setHasSubmittedLocationSearch] =
        useState(false);
    const currentSortOrder = useSelector(selectCurrentSortOrder);
    const filters = useSelector(filtersValuesSelector);
    const isTravelTimeSearch = useSelector(isTravelTimeSearchSelector);

    const [tabSelected, setTabSelected] = useState<Tab>(searchType);

    const isForSale = tabSelected === 'for-sale' || tabSelected === 'new-homes';
    const isHousePricesSearch = tabSelected === 'house-prices';

    const value = locationTextValue || locationValue;

    const handleSearchProperties = () => {
        const hasEnteredAValueOrSelectedFromDropdown = !!value;
        const eventLabel = hasEnteredAValueOrSelectedFromDropdown
            ? `${tabSelected}-success`
            : `${tabSelected}-empty-search`;

        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'search',
            eventLabel
        });

        const excludeRadius = !showRadius;
        if (!value) {
            setHasSubmittedLocationSearch(true);
            return;
        }

        // TODO: Move up/down as necessary depending on whether different tracking required
        if (onSearchClick) {
            onSearchClick({
                filters,
                router
            });
            return;
        }

        searchProperties(
            userAlertId,
            router,
            dispatch,
            tabSelected as SearchPageType,
            searchSource,
            filters,
            currentSortOrder,
            excludeRadius,
            excludePageSize
        );
    };

    const handlePressUpdate = (evt: MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();
        evt.stopPropagation();

        triggerTracking({
            event: EVENT,
            eventCategory: CATEGORY,
            eventAction: 'click',
            eventLabel: 'quick_search_bar_update'
        });

        handleSearchProperties();
    };

    const handleSearchHousePrices = () => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'search',
            // No failure label as the button is disabled if there is no content
            eventLabel: 'house-prices-success'
        });

        searchHousePrices(value, identifier);
    };

    const showError = hasSubmittedLocationSearch && !value;

    return (
        <>
            {!hideTabs && (
                <BuyRentTabs
                    onPress={setTabSelected}
                    selected={tabSelected}
                    includeHousePrices
                    testIdSuffix={'mobile'}
                />
            )}

            {isHousePricesSearch ? (
                <SearchInitationWrapper hasExtraPaddingTop>
                    <HousePricesLocationWrapper>
                        <LocationSearchInput
                            id="header-location"
                            onLocationInputKeyDown={handleSearchHousePrices}
                            hasBorderRight={false}
                            searchType="house-prices"
                            value={locationValue}
                        />
                    </HousePricesLocationWrapper>
                    <StyledButtonWrapperMobile>
                        <StyledButtonMobile
                            type="button"
                            onClick={handleSearchHousePrices}
                            data-testid="mobile-house-prices-search-button"
                            disabled={!value}
                        >
                            {SEARCH_BUTTON_TEXT}
                        </StyledButtonMobile>
                    </StyledButtonWrapperMobile>
                </SearchInitationWrapper>
            ) : (
                <>
                    <LocationFilterWrapper>
                        <LocationSearchInput
                            id="filters-location-mobile"
                            onLocationInputKeyDown={
                                onSearchClick
                                    ? () =>
                                          onSearchClick({
                                              filters,
                                              router
                                          })
                                    : handleSearchProperties
                            }
                            onlyDisplayRemoveIconWhenActive={false}
                            value={locationValue}
                            showError={showError}
                        />
                    </LocationFilterWrapper>

                    {isTravelTimeSearch ? (
                        <>
                            <SelectMaxTravelTime />
                            <SelectTransportMode />{' '}
                        </>
                    ) : showRadius ? (
                        <SelectRadius />
                    ) : null}

                    {isForSale ? (
                        <SelectBedsPriceForSale />
                    ) : (
                        <>
                            <MobileSectionHeadline>
                                {PRICE_RANGE}
                            </MobileSectionHeadline>
                            <SelectPriceToRent />

                            <MobileSectionHeadline>
                                {BEDROOMS}
                            </MobileSectionHeadline>
                            <SelectBedsToRent />
                        </>
                    )}

                    {hidePropertyType ? null : (
                        <SelectPropertyType
                            excludeFarms={searchSource === 'new-homes-dev'}
                        />
                    )}
                    <UpdateButtonWithIcon
                        isFullWidth
                        type="button"
                        onClick={handlePressUpdate}
                        data-testid="update-results-button"
                        title="Update"
                    >
                        <SearchSmall />
                        {updateResultsText}
                    </UpdateButtonWithIcon>
                </>
            )}
        </>
    );
};
